import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  StatusBar,
  SafeAreaView,
  LayoutChangeEvent,
  Animated,
  Easing,
} from 'react-native';
import { colors, spacing } from '../theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useIsMobileBrowser } from '../hooks/useIsMobileBrowser';
import { useRightPaneWidth } from '../hooks/useRightPaneWidth';
import { useAppStore } from '../store/appStore';
import { PANE_ANIMATION_DURATION } from '../constants/paneLayout';
import { BottomTabs } from './BottomTabs';
import { RightPane } from './RightPane';
import { LeftPane } from './LeftPane';
import { ScreenHeaderHeightProvider } from '../contexts/ScreenHeaderHeightContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useTheme } from '../contexts/ThemeContext';

interface ScreenLayoutProps {
  children: React.ReactNode;
  showBottomTabs?: boolean;
  header?: React.ReactNode;
  onTabPress?: (tabId: string) => void;
  showLeftPane?: boolean;
  onLeftPaneClose?: () => void;
  leftPaneContent?: 'settings' | 'contacts' | 'custom';
  leftPaneCustomContent?: React.ReactNode;
  onSettingsItemPress?: (itemId: string) => void;
  activeSettingsItemId?: string | null;
  showRightPane?: boolean;
  onRightPaneClose?: () => void;
  rightPaneContent?: 'contacts' | 'edit-contact' | 'details' | 'custom' | 'empty' | 'settings';
  rightPaneCustomContent?: React.ReactNode;
  /** What the right pane shows (chat, call history, a form…): the icon and label of its tab beside the AI assistant. */
  rightPaneTab?: 'chat' | 'calls' | 'form';
  selectedContact?: any;
  onContactSave?: (contact: any) => void;
  onContactDelete?: (contactId: string) => void;
  onContactPress?: (contact: any) => void;
  /** Passed straight through to RightPane's contactsDataListId (and, from
   * there, ContactEditForm's own `listId`) — set when the caller's Contacts
   * screen is in a system Data List's scoped mode (item 2's Extra Fields). */
  contactsDataListId?: string;
  contactsRefreshTrigger?: number; // Trigger refresh when this value changes
  /** Id of the contact currently open elsewhere (the screen's own middle
   * content, or an embedded conversation in the right pane) — passed through
   * to both LeftPane and RightPane's own 'contacts' ContactsList instance so
   * that row can show a visual "active" mark. */
  activeContactId?: string;
  settingsContent?: React.ReactNode; // Settings content to display in right pane
  /** Reserved vertical space (px, web only) for the fixed header, used only
   * until the header's real height is measured via onLayout (see
   * effectiveHeaderHeight below) — defaults to the standard single-row
   * Header's approximate height. Screens that omit `header` entirely (e.g.
   * the flow editor, which needs full viewport height for its canvas)
   * should pass 0 so content/panes aren't left with a dead gap where the
   * header would have been. */
  headerHeight?: number;
  /** Opts a screen out of contentArea's default horizontal padding — for
   * screens that already pad their own root content, or that need a
   * full-bleed layout (edge-to-edge lists/canvases). */
  disableScreenPadding?: boolean;
  /** Fires with the header's real measured height (web only) whenever it
   * changes — for screens that render their own position:fixed elements
   * (e.g. ConversationScreen's header/input bar) and need to align their
   * `top`/`bottom` offsets with the actual header height instead of
   * guessing a static value that drifts whenever the header's content
   * changes (e.g. the desktop nav row showing/hiding). */
  onHeaderHeightChange?: (height: number) => void;
  /** Overrides the shared/persisted right-pane width for just this screen —
   * see RightPane's widthOverride for why this isn't a global width change. */
  rightPaneWidthOverride?: number;
  /** FormHeader (title/back/actions row), rendered as a real sibling of the
   * scrollable content area — NOT inside `children`. A position:fixed
   * element nested inside contentAreaScroll (the actual overflow:auto
   * scroll box) is unreliable in Chrome for stacking/paint order — scrolled
   * content can end up painting over it regardless of z-index, the same
   * class of bug already fixed once for `header` (the app-level nav bar)
   * by keeping IT out of the scrollable tree entirely. This prop does the
   * same for FormHeader instead of asking every screen to work around the
   * nesting problem individually. */
  formHeader?: React.ReactNode;
}

export const ScreenLayout: React.FC<ScreenLayoutProps> = ({
  children,
  showBottomTabs = true,
  header,
  onTabPress,
  // Nearly every screen was independently computing this exact same value
  // itself (`const [showLeftPane] = useState(Platform.OS === 'web')`,
  // copy-pasted ~85 times) just to pass it straight back in — centralizing
  // it here as the default means a screen that wants the ordinary behavior
  // can simply omit the prop; one that genuinely needs something different
  // still overrides it explicitly exactly as before, so this changes
  // nothing for any screen already passing an explicit value.
  showLeftPane = Platform.OS === 'web',
  onLeftPaneClose,
  leftPaneContent = 'settings',
  leftPaneCustomContent,
  onSettingsItemPress,
  activeSettingsItemId,
  showRightPane = Platform.OS === 'web',
  onRightPaneClose,
  // Confirmed real bug, not just a theoretical risk: 64 of the ~96 screens
  // using ScreenLayout never pass rightPaneContent at all, so every one of
  // them — Orders, Vendors, Catalogs, Devices, Departments, Events, Visits,
  // all of it, nothing to do with contacts — was silently getting 'contacts'
  // here and mounting a full compact ContactsList in its right pane purely
  // because that happened to be this default. Each one independently fetches
  // its own tag list, runs its own BulkUploadProgressIndicator 3s poll loop,
  // etc. — real duplicated network traffic app-wide, not specific to the
  // actual Contacts screen (which now sets its own 'empty' default
  // explicitly anyway — see ContactsListScreen.tsx). 'empty' is the correct
  // default; a screen that genuinely wants a contacts picker in its pane
  // should opt in explicitly.
  rightPaneContent = 'empty',
  rightPaneCustomContent,
  rightPaneTab,
  selectedContact,
  onContactSave,
  onContactDelete,
  onContactPress,
  contactsDataListId,
  contactsRefreshTrigger,
  activeContactId,
  settingsContent,
  headerHeight = 84,
  disableScreenPadding = false,
  onHeaderHeightChange,
  rightPaneWidthOverride,
  formHeader,
}) => {
  const styles = useThemedStyles(getStyles);
  const { themeMode } = useTheme();
  // Real rendered height of the docked bottom tab bar (includes the iOS
  // home-indicator inset BottomTabs pads itself with). The bar floats over
  // the content (absolute/fixed), so the content reserves exactly this much
  // at its bottom — otherwise a screen's last fields scroll under the bar.
  const [bottomTabsHeight, setBottomTabsHeight] = useState(0);
  const insets = useSafeAreaInsets();
  const { setActiveTab, navigateTo, showNotification } = useAppContext();
  const { isRTL } = useLocale();
  const isMobileBrowser = useIsMobileBrowser();

  // The fixed-position web header's actual rendered height varies with its
  // content (e.g. whether the desktop nav row is showing) — measure it
  // instead of trusting the caller-supplied headerHeight guess, which
  // otherwise leaves a dead gap (or clips content) whenever the header's
  // real height doesn't match that prop.
  const [measuredHeaderHeight, setMeasuredHeaderHeight] = useState<number | null>(null);
  const effectiveHeaderHeight = Platform.OS === 'web' && measuredHeaderHeight != null ? measuredHeaderHeight : headerHeight;
  const handleHeaderLayout = (e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    if (height > 0 && height !== measuredHeaderHeight) {
      setMeasuredHeaderHeight(height);
    }
  };

  useEffect(() => {
    onHeaderHeightChange?.(effectiveHeaderHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveHeaderHeight]);

  // formHeader is pinned the same way RightPane pins itself — real
  // position:absolute/fixed anchored to a non-scrolling ancestor, not flex
  // flow — since flex flow only keeps it in place as long as nothing above
  // it in the DOM ever needs more room than its flex-basis allows (measured
  // height varies per screen — e.g. DataListSchemaScreen stacks a tab row
  // under it — so it's measured the same way the app header's height is).
  const [measuredFormHeaderHeight, setMeasuredFormHeaderHeight] = useState<number | null>(null);
  const effectiveFormHeaderHeight = measuredFormHeaderHeight ?? 0;
  const handleFormHeaderLayout = (e: LayoutChangeEvent) => {
    const height = e.nativeEvent.layout.height;
    if (height > 0 && height !== measuredFormHeaderHeight) {
      setMeasuredFormHeaderHeight(height);
    }
  };

  // Content-area margins animate in lockstep with LeftPane/RightPane's own
  // collapse animation (same duration/easing, triggered by the same store
  // fields) rather than snapping after the pane finishes — each pane owns
  // its own Animated.Value internally, so this runs a parallel instance off
  // the same source of truth instead of trying to share one across files.
  const { leftPaneCollapsed, leftPaneWidth, rightPaneCollapsed } = useAppStore();
  const rightPaneWidth = useRightPaneWidth(rightPaneWidthOverride);
  const leftMarginAnim = useRef(new Animated.Value(leftPaneCollapsed ? 0 : leftPaneWidth)).current;
  const rightMarginAnim = useRef(new Animated.Value(rightPaneCollapsed ? 0 : rightPaneWidth)).current;

  useEffect(() => {
    Animated.timing(leftMarginAnim, {
      toValue: leftPaneCollapsed ? 0 : leftPaneWidth,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [leftPaneCollapsed, leftPaneWidth, leftMarginAnim]);

  useEffect(() => {
    Animated.timing(rightMarginAnim, {
      toValue: rightPaneCollapsed ? 0 : rightPaneWidth,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [rightPaneCollapsed, rightPaneWidth, rightMarginAnim]);

  // Use passed onTabPress or create default handler using context
  const handleTabPress = onTabPress || ((tabId: string) => {
    setActiveTab(tabId);
    switch (tabId) {
      case 'home':
        navigateTo('home');
        break;
      case 'chat':
        navigateTo('chat');
        break;
      case 'social':
        navigateTo('social');
        break;
      case 'contacts':
        navigateTo('contacts');
        break;
      case 'products':
        // Navigate to unified products management screen
        navigateTo('products');
        break;
      case 'performance':
        navigateTo('performance');
        break;
      case 'more':
        // More modal handled by BottomTabs component
        break;
    }
  });
  
  return (
    <View style={styles.container}>
      {/* Status bar follows the theme: the header behind it (brand.chrome)
          is a light surface in light mode and dark navy in dark mode, so
          the clock/Wi-Fi glyphs must be dark in light mode — a fixed
          light-content (from the old blue header) made them vanish. */}
      <StatusBar 
        barStyle={themeMode === 'dark' ? 'light-content' : 'dark-content'} 
        backgroundColor={colors.brand.chrome as string}
        translucent={Platform.OS === 'android'}
        hidden={false}
      />
      
      {/* Safe Area for Status Bar - iOS uses SafeAreaView, Android uses manual insets */}
      {Platform.OS === 'ios' ? (
        <SafeAreaView style={styles.safeAreaTop} />
      ) : (
        <View style={[styles.safeAreaTop, { paddingTop: insets.top }]} />
      )}
      
      {/* Header Area - Render normally for mobile, fixed for web.
          pointerEvents="box-none" on web: this wrapper spans the full
          viewport width/height at a high zIndex, but should only intercept
          clicks on its actual visible header content — otherwise it can
          swallow clicks meant for pane content directly beneath its box
          (e.g. a right-pane header button sitting right at the boundary
          where the measured header height ends). */}
      {header && (
        <View
          style={[
            styles.headerArea,
            Platform.OS === 'web' && styles.webHeaderArea
          ]}
          onLayout={Platform.OS === 'web' ? handleHeaderLayout : undefined}
          pointerEvents={Platform.OS === 'web' ? 'box-none' : 'auto'}
        >
          {header}
        </View>
      )}

      {/* Main Content Area — margins animate in lockstep with the panes'
          own collapse animation via leftMarginAnim/rightMarginAnim (same
          store-driven width, not the old static 355 literal). This outer
          layer stays overflow:visible (RightPane.tsx's wrapper/container
          split is the reference — a position:fixed/relative ancestor that
          ALSO clips via overflow ends up trapping any position:fixed
          descendant's paint/stacking, which is what broke FormHeader
          staying above scrolled content despite a higher z-index). The
          actual scroll clipping happens on styles.contentAreaScroll below,
          a plain (non-positioned) inner box. */}
      <Animated.View
        style={[
        styles.contentArea,
        Platform.OS === 'web' && ({
          position: 'relative',
        } as any),
        showLeftPane && Platform.OS === 'web' && !isMobileBrowser && {
          [isRTL ? 'marginRight' : 'marginLeft']: leftMarginAnim,
        },
        showRightPane && Platform.OS === 'web' && !isMobileBrowser && {
          [isRTL ? 'marginLeft' : 'marginRight']: rightMarginAnim,
        },
      ]}>
        {/* formHeader — pinned the same way RightPane is: position:absolute
            against contentArea (its nearest position:relative ancestor,
            which never scrolls and already carries the left/right pane
            margins), not a flex-flow sibling. contentArea isn't a descendant
            of contentAreaScroll (the actual overflow:auto box) — they're
            siblings — so this never revives the original position:fixed-
            inside-a-scrolling-ancestor stacking bug; that bug only applied
            when FormHeader was nested INSIDE the scrolling box itself.
            top:effectiveHeaderHeight clears the app header's own fixed
            overlay (contentArea starts at y:0 same as the header, which
            floats over it — see webHeaderArea). left:0/right:0 fills
            contentArea's own (already pane-margined) box exactly. */}
        {formHeader && Platform.OS === 'web' && (
          <View
            onLayout={handleFormHeaderLayout}
            style={[
              !disableScreenPadding && styles.contentAreaPadded,
              // Phone browsers: match the body below, whose own padding is
              // dropped to 0 (see contentAreaScroll) and replaced by the
              // form's 12px gutter (usePhoneFormGutter) — 32px here left
              // the pinned header misaligned with the form under it.
              !disableScreenPadding && isMobileBrowser && { paddingHorizontal: 12 },
              {
                position: 'absolute',
                top: effectiveHeaderHeight,
                left: 0,
                right: 0,
                zIndex: 500,
              } as any,
            ]}
          >
            <ScreenHeaderHeightProvider value={{ headerHeight: header ? effectiveHeaderHeight : 0, isPinnedByLayout: true }}>
              {formHeader}
            </ScreenHeaderHeightProvider>
          </View>
        )}
        {formHeader && Platform.OS !== 'web' && (
          <View style={!disableScreenPadding && styles.contentAreaPadded}>
            <ScreenHeaderHeightProvider value={{ headerHeight: header ? effectiveHeaderHeight : 0, isPinnedByLayout: true }}>
              {formHeader}
            </ScreenHeaderHeightProvider>
          </View>
        )}
        <View
          style={[
            styles.contentAreaScroll,
            !disableScreenPadding && styles.contentAreaPadded,
            showBottomTabs && bottomTabsHeight > 0 && { paddingBottom: bottomTabsHeight },
            Platform.OS === 'web' && ({
              paddingTop: formHeader ? effectiveHeaderHeight + effectiveFormHeaderHeight : effectiveHeaderHeight,
              ...(isMobileBrowser && { paddingHorizontal: 0, width: '100%' }),
            } as any),
          ]}
        >
          <ScreenHeaderHeightProvider value={{ headerHeight: header ? effectiveHeaderHeight : 0, isPinnedByLayout: false }}>
            {children}
          </ScreenHeaderHeightProvider>
        </View>
      </Animated.View>

      {/* Left Pane — spans the full viewport height from the screen's top
          edge (topOffset 0), not just below the header, so it runs the
          entire left edge of the screen. Its own zIndex (90) sits below
          the header's (webHeaderArea, zIndex 1000) so the header still
          renders on top where they'd otherwise overlap. */}
      {showLeftPane && Platform.OS === 'web' && !isMobileBrowser && (
        <LeftPane
          isVisible={showLeftPane}
          onClose={onLeftPaneClose}
          content={leftPaneContent}
          customContent={leftPaneCustomContent}
          onSettingsItemPress={onSettingsItemPress}
          activeSettingsItemId={activeSettingsItemId}
          topOffset={effectiveHeaderHeight}
          onContactPress={onContactPress}
          contactsDataListId={contactsDataListId}
          contactsRefreshTrigger={contactsRefreshTrigger}
          activeContactId={activeContactId}
        />
      )}

      {/* Right Pane - Only show on desktop web, not mobile browsers.
          Spans the full viewport height from the screen's top edge, same
          reasoning as LeftPane above. */}
          {showRightPane && Platform.OS === 'web' && !isMobileBrowser && (
            <RightPane
              isVisible={showRightPane}
              onClose={onRightPaneClose}
              content={rightPaneContent}
              customContent={rightPaneCustomContent}
              tab={rightPaneTab}
              selectedContact={selectedContact}
              onContactSave={onContactSave}
              onContactDelete={onContactDelete}
              onContactPress={onContactPress}
              contactsDataListId={contactsDataListId}
              contactsRefreshTrigger={contactsRefreshTrigger}
              activeContactId={activeContactId}
              settingsContent={settingsContent}
              topOffset={effectiveHeaderHeight}
              widthOverride={rightPaneWidthOverride}
            />
          )}
      
      {/* Bottom Tabs - Show when showBottomTabs is true. On web this only
          renders for mobile-web (see showBottomTabs wiring in HomeScreen),
          so it must be truly fixed/bottom-docked like native's absolute
          positioning — 'relative' here would let it flow inline after
          contentArea instead of docking to the viewport bottom. */}
      {showBottomTabs && (
        <View
          onLayout={(event) => {
            const measured = Math.round(event.nativeEvent.layout.height);
            if (measured > 0 && measured !== bottomTabsHeight) setBottomTabsHeight(measured);
          }}
          style={[
          styles.bottomTabsContainer,
          {
            position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            // No paddingBottom here - BottomTabs component handles safe area internally
          }
        ]}>
          <BottomTabs onTabPress={handleTabPress} />
        </View>
      )}
      
      {/* Bottom Safe Area for Tabs - iOS uses SafeAreaView, Android uses manual insets */}
      {!showBottomTabs && (
        Platform.OS === 'ios' ? (
          <SafeAreaView style={styles.safeAreaBottom} />
        ) : (
          <View style={[styles.safeAreaBottom, { paddingBottom: insets.bottom }]} />
        )
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.base,
    ...(Platform.OS === 'web' && {
      // Pinned directly to the viewport's four edges — the same proven
      // technique already used by webHeaderArea/RightPane/bottomTabsContainer
      // in this file — instead of position:relative + height:'100vh' (which
      // required an `as any` cast to satisfy ViewStyle's DimensionValue type,
      // a sign React Native Web's style compiler doesn't reliably treat
      // '100vh' as a real CSS length: confirmed live via devtools — the
      // rendered element's computed height/overflow didn't match what was
      // specified at all, while page-level `<html>` ended up the actual
      // scroller, carrying the pinned formHeader along with it). Four fixed
      // insets need no DimensionValue parsing at all, so there's nothing for
      // the compiler to mis-handle.
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
    }),
  },
  safeAreaTop: {
    backgroundColor: colors.brand.chrome,
  },
  headerArea: {
    backgroundColor: colors.brand.chrome,
  },
  contentArea: ({
    flex: 1,
    backgroundColor: colors.background.base,
    // minHeight:0 overrides the flex-item default of min-height:auto, which
    // otherwise lets this box grow to fit contentAreaScroll's full content
    // height instead of respecting its flex:1 share of container's 100vh —
    // once that happens, container's overflow:hidden clips it visually but
    // contentAreaScroll's own overflow:auto never actually engages (there's
    // nothing left to constrain it to), so the whole page scrolls natively
    // and formHeader (a normal, non-fixed flex sibling) scrolls away with it.
    ...(Platform.OS === 'web' && { minHeight: 0 } as any),
  } as any),
  // The actual scroll box — deliberately a separate, non-positioned node
  // from styles.contentArea above (see the comment at its usage site).
  contentAreaScroll: ({
    flex: 1,
    ...(Platform.OS === 'web' && { overflowY: 'auto', height: '100%', minHeight: 0 }),
  } as any),
  contentAreaPadded: {
    paddingHorizontal: spacing.screenPadding,
  },
  safeAreaBottom: {
    backgroundColor: colors.background.base,
  },
  bottomTabsContainer: {
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    zIndex: 100000, // Highest z-index to ensure it's always on top
    elevation: 100, // Highest elevation for Android
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      width: '100%',
    }),
  },
  webHeaderArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    elevation: 1000,
    width: '100%',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any, // Use fixed positioning on web for true fixed behavior
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    }),
  }
});
